import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Button, Dropdown, Form, Spinner, Table,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import ListPager from '../../../Components/ListPager';
import AiStatusDot from '../Components/AiStatusDot';
import AiDecisionModal from '../Components/AiDecisionModal';
import AiActivityService, {
  DECISION_STATES,
  NEEDS_REVIEW_STATE,
  outcomeDescriptor,
  stateDescriptor,
} from '../../../Services/AiActivityService';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * The Activity area of AI Admin: every decision an AI function has taken.
 *
 * An AREA, registered in ../areas.js and rendered inside the AI Admin shell
 * (../index.jsx) at /ai-admin/activity. The SHELL owns the eyebrow, the
 * page's <h1> and the area selector; this file renders no heading of its own,
 * because there is one <h1> per page and it is the area's label.
 *
 * ── ONE TABLE IS THE PRODUCT ──
 * A filter row, a row per decision, and the row opens the detail. Function
 * health lives in the Function filter as a status dot rather than on a
 * separate board: one fewer panel to read, and you pass it on the way to
 * filtering anyway. No charts — there is nothing worth charting yet, and a
 * chart of four rows is decoration.
 *
 * ── READ-ONLY, AND THEREFORE NO BUTTONS THAT ACT ──
 * The collection is append-only and this phase observes. Approve, reject and
 * re-run are a later step; offering them now would mean a control that does
 * nothing.
 *
 * ── THE FUNCTION FILTER COMES FROM THE RESPONSE, NOT FROM THE ROWS ──
 * `functions` rides along with every page, from the code-owned registry. Nine
 * of the ten functions record nothing today: derive the filter from the rows
 * and nine of them would be missing, when "this one has never logged
 * anything" is the most useful thing the screen can tell you. The dot is how
 * it tells you — hollow means nothing from that function is in what is
 * listed.
 *
 * ── PERMISSION ──
 * No in-screen check. Every control here is a reading control and the route
 * and the nav entry already gate the area on `ManageAi` (Routes.jsx,
 * config/navigation.js), exactly as Agent Instructions reasons. There is no
 * ungated half of this one.
 */

/** The server's own default page size. Mirrored so page 1 asks for one page. */
const DEFAULT_LIMIT = 50;

const BLANK_FILTERS = {
  ai_function: '', state: '', subject_reference: '', from: '', to: '',
};

/**
 * What the reference box is asking for, said by example rather than by a
 * sentence. The server matches `subject_reference` EXACTLY — a whole value an
 * operator copies, answered by the `ai_decision_subject_at` index with a seek
 * — so a placeholder shaped like a real reference teaches that in a way the
 * word "search" would actively undo.
 */
const REFERENCE_EXAMPLE = 'PU_DASH_1699241';

const readErr = (err, fallback) => (
  err.response?.data?.message || err.message || fallback
);

/**
 * One function's dot, from the decisions currently listed.
 *
 * AMBER WINS OVER RED, because amber is the one that is somebody's job: a
 * function with a row awaiting review AND a row that broke needs a human
 * either way, and the human is the actionable half.
 *
 * It reads the LOADED PAGE and nothing else, because that is the only
 * per-function information the response carries — there are no server-side
 * counts, and inventing a health field here would be a number nobody could
 * check. So `title` says what it counted.
 */
export function functionTone(key, decisions) {
  const mine = decisions.filter((d) => d.ai_function === key);
  if (mine.length === 0) return { tone: 'muted', title: 'Nothing in this list' };
  if (mine.some((d) => d.state === NEEDS_REVIEW_STATE)) {
    return { tone: 'warn', title: 'Awaiting review in this list' };
  }
  if (mine.some((d) => d.state === 'failed' || (d.tool_outcome && d.tool_outcome !== 'ok'))) {
    return { tone: 'bad', title: 'Denied, degraded or failed in this list' };
  }
  return { tone: 'good', title: `${mine.length} in this list` };
}

function ActivityArea() {
  const [filters, setFilters] = useState(BLANK_FILTERS);
  // The reference box is typed into, so it holds its own draft until the
  // filter is committed. See `commitReference`.
  const [reference, setReference] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const [decisions, setDecisions] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1, limit: DEFAULT_LIMIT, total: 0, pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await AiActivityService.list({ ...filters, page, limit });
      setDecisions(result?.decisions || []);
      setPagination(result?.pagination || {
        page, limit, total: 0, pages: 0,
      });
      // Served with every page from the registry, so it is simply taken from
      // the latest response rather than held across one.
      setFunctions(result?.functions || []);
    } catch (err) {
      setDecisions([]);
      setError(readErr(err, 'Failed to load the AI activity'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  /** Any filter change goes back to page 1 — page 4 of a new filter is a lie. */
  const setFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  /**
   * Commit the reference on Enter or on leaving the field, NEVER per
   * keystroke: the server matches it exactly, so a half-typed reference
   * matches nothing — a live filter would blank the table for every one of a
   * fifteen-character reference's characters and fire fifteen requests doing
   * it. Only a CHANGED value is committed, so tabbing through costs nothing.
   */
  const commitReference = () => {
    if (reference === filters.subject_reference) return;
    setFilter({ subject_reference: reference });
  };

  const clearAll = () => {
    setFilters(BLANK_FILTERS);
    setReference('');
    setPage(1);
  };

  const needsReview = filters.state === NEEDS_REVIEW_STATE;
  const filtered = Object.values(filters).some((v) => v !== '');

  const labelFor = (key) => (
    functions.find((fn) => fn.key === key)?.label || key || '—'
  );

  const activeFunctionLabel = filters.ai_function
    ? labelFor(filters.ai_function)
    : 'All';

  return (
    <>
      <Error error={error} setError={setError} />

      <div className="ai-activity-filters">
        {/* A Dropdown rather than a <select>, because a native <option>
            cannot carry the status dot and the dot is the function board. */}
        <Dropdown
          onSelect={(key) => setFilter({ ai_function: key === 'all' ? '' : key })}
        >
          <Dropdown.Toggle variant="outline-secondary" size="sm" id="ai-activity-function">
            {`Function: ${activeFunctionLabel}`}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item as="button" type="button" eventKey="all" active={!filters.ai_function}>
              All
            </Dropdown.Item>
            <Dropdown.Divider />
            {functions.map((fn) => {
              const { tone, title } = functionTone(fn.key, decisions);
              return (
                <Dropdown.Item
                  as="button"
                  type="button"
                  key={fn.key}
                  eventKey={fn.key}
                  active={filters.ai_function === fn.key}
                >
                  <AiStatusDot tone={tone} title={title} />
                  {fn.label || fn.key}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>

        <Form.Select
          size="sm"
          aria-label="State"
          value={filters.state}
          onChange={(e) => setFilter({ state: e.target.value })}
        >
          <option value="">State: All</option>
          {DECISION_STATES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </Form.Select>

        {/* Exact match, not a search — the placeholder is a real-looking
            reference because that says so better than a label would. */}
        <Form.Control
          size="sm"
          type="text"
          aria-label="Subject reference"
          placeholder={REFERENCE_EXAMPLE}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          onBlur={commitReference}
          onKeyDown={(e) => { if (e.key === 'Enter') commitReference(); }}
        />

        <Form.Control
          size="sm"
          type="date"
          aria-label="From"
          value={filters.from}
          onChange={(e) => setFilter({ from: e.target.value })}
        />
        <Form.Control
          size="sm"
          type="date"
          aria-label="To"
          value={filters.to}
          onChange={(e) => setFilter({ to: e.target.value })}
        />

        {/* The pill and the State select are ONE piece of state, so they can
            never disagree: the pill is the shortcut to the queue that wants a
            human, and picking another state in the select turns it off. */}
        <Button
          size="sm"
          variant={needsReview ? 'warning' : 'outline-warning'}
          className="ai-activity-pill"
          aria-pressed={needsReview}
          onClick={() => setFilter({ state: needsReview ? '' : NEEDS_REVIEW_STATE })}
        >
          <i className="fa fa-user-clock me-2" aria-hidden="true" />
          Needs review
        </Button>

        {filtered && (
          <Button
            size="sm"
            variant="link"
            onClick={clearAll}
          >
            Clear
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading activity…</p>
        </div>
      ) : (
        <>
          <Table
            responsive
            striped
            bordered
            hover
            className="responsive-card-table"
            aria-label="AI decisions"
          >
            <thead>
              <tr>
                <th>Time</th>
                <th>Function</th>
                <th>Subject</th>
                <th>Outcome</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {decisions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    <i className="fa fa-wave-square fa-2x mb-2 d-block opacity-50" aria-hidden="true" />
                    {filtered
                      ? 'Nothing matches these filters.'
                      : `Nothing recorded yet. ${functions.length} function${functions.length === 1 ? '' : 's'} can write here.`}
                  </td>
                </tr>
              ) : decisions.map((decision) => {
                const state = stateDescriptor(decision.state);
                const outcome = outcomeDescriptor(decision.tool_outcome);
                return (
                  <tr
                    // eslint-disable-next-line no-underscore-dangle
                    key={decision._id}
                    className={decision.state === NEEDS_REVIEW_STATE ? 'table-warning' : undefined}
                  >
                    <td data-label="Time">
                      {/* The row's affordance lives on the first cell, the
                          way the Knowledge Bases table opens a base: a real
                          button, so it is reachable by keyboard. */}
                      <Button
                        variant="link"
                        className="p-0 text-start"
                        onClick={() => setOpen(decision)}
                      >
                        {formatDateTime(decision.occurred_at)}
                      </Button>
                    </td>
                    <td data-label="Function">{labelFor(decision.ai_function)}</td>
                    <td data-label="Subject">
                      {decision.subject?.reference || decision.subject?.id || '—'}
                    </td>
                    <td data-label="Outcome">
                      {outcome && outcome.value !== 'ok'
                        ? <Badge bg="danger">{outcome.label}</Badge>
                        : <span className="text-muted">{outcome ? outcome.label : '—'}</span>}
                    </td>
                    <td data-label="State">
                      <span className="ai-state">
                        {state && <AiStatusDot tone={state.tone} />}
                        {state ? state.label : '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <ListPager
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={(next) => { setLimit(next); setPage(1); }}
          />
        </>
      )}

      {open && (
        <AiDecisionModal
          decision={open}
          functionLabel={labelFor(open.ai_function)}
          onHide={() => setOpen(null)}
        />
      )}
    </>
  );
}

export default ActivityArea;
